import dayjs from "dayjs";
import { OrderStatus } from "../../enums";

export type LogisticsPathEntryLike = {
  type?: string;
  customerId?: string;
  supplierId?: string;
  isCurrentLocation?: boolean;
  isDestination?: boolean;
  completedShipmentId?: string;
  arrivedAt?: string | Date;
  address?: {
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    pinCode?: string;
    contact?: {
      name?: string;
      phoneNumber?: string;
    };
  };
};

export type OrderTrackingShipmentLike = {
  deliveryTracking?: {
    trackingStatus?: string;
    lastUpdatedAt?: string | Date;
    deliveryAgent?: {
      name?: string;
      phoneNumber?: string;
    };
    timeline?: {
      pickedUpAt?: string | Date;
      deliveredAt?: string | Date;
    };
  };
  deliveryBooking?: {
    trackingUrl?: string;
    bookedAt?: string | Date;
  };
  status?: string;
};

export type OrderTrackingOrderLike = {
  orderNo?: string;
  orderStatus?: string;
  shipmentId?: string;
  customerId?: string;
  logisticsPath?: LogisticsPathEntryLike[] | null;
  metaInfo?: {
    dispatchTime?: string | Date;
    dispatchRider?: {
      name?: string;
      phone?: string;
      vehicleNumber?: string;
      trackingUrl?: string;
    };
  };
};

export type JourneyNodeVisual = "green" | "blue" | "neutral" | "grey";
export type JourneyConnectorVisual = "green" | "blue" | "grey";

export type JourneyNodeStatus =
  | "Dispatched"
  | "In Transit"
  | "Arrived"
  | "Pending"
  | "Delivered"
  | "At Stop";

export type JourneyNode = {
  id: string;
  name: string;
  cityState: string;
  statusLabel: JourneyNodeStatus;
  visual: JourneyNodeVisual;
  timestamp?: string;
  isCurrentLocation: boolean;
  isDestination: boolean;
  type?: string;
};

export type OrderTrackingJourney = {
  orderNo: string;
  nodes: JourneyNode[];
  connectors: JourneyConnectorVisual[];
  overallBadge: string;
  lastUpdatedLabel?: string;
  bannerText: string;
  isInTransit: boolean;
  trackingUrl?: string;
  riderName?: string;
  riderPhone?: string;
};

const IN_TRANSIT_STATUSES = new Set<string>([
  OrderStatus.DISPATCHED,
  OrderStatus.SHIPPED,
]);

function formatJourneyTimestamp(
  value?: string | Date | null,
): string | undefined {
  if (!value) return undefined;
  const parsed = dayjs(value);
  if (!parsed.isValid()) return undefined;
  return parsed.format("DD MMM, hh:mm A");
}

function entryKey(entry: LogisticsPathEntryLike, index: number): string {
  const id =
    entry.supplierId?.toString() ||
    entry.customerId?.toString() ||
    entry.address?.addressLine1 ||
    String(index);
  return `${entry.type || "STOP"}:${id}:${index}`;
}

function stopName(entry: LogisticsPathEntryLike, index: number): string {
  const contactName = entry.address?.contact?.name?.trim();
  if (contactName) return contactName;
  if (entry.type === "CUSTOMER") return "Customer";
  if (entry.type === "SUPPLIER") return "Supplier";
  return `Stop ${index + 1}`;
}

function cityState(entry: LogisticsPathEntryLike): string {
  const city = entry.address?.city?.trim();
  const state = entry.address?.state?.trim();
  if (city && state) return `${city}, ${state}`;
  return city || state || "";
}

function sameStop(
  a: LogisticsPathEntryLike | undefined,
  b: LogisticsPathEntryLike | undefined,
): boolean {
  if (!a || !b) return false;
  if (a.type && b.type && a.type !== b.type) return false;
  if (a.supplierId && b.supplierId) {
    return a.supplierId.toString() === b.supplierId.toString();
  }
  if (a.customerId && b.customerId) {
    return a.customerId.toString() === b.customerId.toString();
  }
  return (
    Boolean(a.address?.addressLine1) &&
    a.address?.addressLine1 === b.address?.addressLine1
  );
}

function findFlaggedIndex(
  path: LogisticsPathEntryLike[],
  flag: "isCurrentLocation" | "isDestination",
): number {
  for (let i = path.length - 1; i >= 0; i--) {
    if (path[i]?.[flag] === true) return i;
  }
  return -1;
}

function isCustomerDelivered(
  entry: LogisticsPathEntryLike,
  orderStatus?: string,
): boolean {
  return (
    entry.type === "CUSTOMER" &&
    (Boolean(entry.arrivedAt) || orderStatus === OrderStatus.DELIVERED)
  );
}

function latestArrivedAt(
  path: LogisticsPathEntryLike[],
): string | Date | undefined {
  let latest: { value: string | Date; ms: number } | undefined;
  for (const entry of path) {
    if (!entry.arrivedAt) continue;
    const ms = dayjs(entry.arrivedAt).valueOf();
    if (!Number.isFinite(ms)) continue;
    if (!latest || ms > latest.ms) {
      latest = { value: entry.arrivedAt, ms };
    }
  }
  return latest?.value;
}

/**
 * Display order for the real order flow (not raw array order):
 * 1) Untimed origins that appear before the first timed stop in the path
 * 2) All stops with arrivedAt sorted by time (including CUSTOMER)
 * 3) Remaining untimed stops (live current / pending dest) in path order
 *
 * When a customer already has arrivedAt, supplier stops that arrived *after*
 * that delivery (or sit after the customer in the array with no time) are
 * treated as path noise and omitted from the journey.
 */
export function orderLogisticsPathForDisplay(
  path: LogisticsPathEntryLike[],
): Array<{ entry: LogisticsPathEntryLike; originalIndex: number }> {
  const indexed = path.map((entry, originalIndex) => ({
    entry,
    originalIndex,
  }));

  let customerDeliveredMs: number | undefined;
  let customerDeliveredOriginalIndex: number | undefined;
  for (const item of indexed) {
    if (item.entry.type !== "CUSTOMER" || !item.entry.arrivedAt) {
      continue;
    }
    const ms = dayjs(item.entry.arrivedAt).valueOf();
    if (!Number.isFinite(ms)) {
      continue;
    }
    if (
      customerDeliveredMs == null ||
      ms < customerDeliveredMs
    ) {
      customerDeliveredMs = ms;
      customerDeliveredOriginalIndex = item.originalIndex;
    }
  }

  const isExcludedNoise = (item: {
    entry: LogisticsPathEntryLike;
    originalIndex: number;
  }) => {
    if (customerDeliveredMs == null) {
      return false;
    }
    if (item.entry.type === "CUSTOMER") {
      return false;
    }
    if (item.entry.arrivedAt) {
      const ms = dayjs(item.entry.arrivedAt).valueOf();
      return Number.isFinite(ms) && ms > customerDeliveredMs!;
    }
    // Untimed suppliers after a delivered customer in the stored array
    return (
      customerDeliveredOriginalIndex != null &&
      item.originalIndex > customerDeliveredOriginalIndex
    );
  };

  const eligible = indexed.filter((item) => !isExcludedNoise(item));

  const timed = eligible
    .filter(({ entry }) => Boolean(entry.arrivedAt))
    .sort(
      (a, b) =>
        dayjs(a.entry.arrivedAt).valueOf() - dayjs(b.entry.arrivedAt).valueOf(),
    );

  const timedIndexes = new Set(timed.map((item) => item.originalIndex));
  const firstTimedOriginalIndex =
    timed.length > 0
      ? Math.min(...timed.map((item) => item.originalIndex))
      : Number.POSITIVE_INFINITY;

  const untimedBefore = eligible.filter(
    ({ entry, originalIndex }) =>
      !timedIndexes.has(originalIndex) &&
      !entry.arrivedAt &&
      originalIndex < firstTimedOriginalIndex,
  );

  const untimedAfter = eligible
    .filter(
      ({ entry, originalIndex }) =>
        !timedIndexes.has(originalIndex) &&
        !entry.arrivedAt &&
        originalIndex >= firstTimedOriginalIndex,
    )
    .sort((a, b) => a.originalIndex - b.originalIndex);

  return [...untimedBefore, ...timed, ...untimedAfter];
}

/**
 * Pure mapping: logisticsPath + order status (+ optional shipment) → horizontal journey.
 */
export function buildOrderTrackingJourney(
  order: OrderTrackingOrderLike,
  shipment?: OrderTrackingShipmentLike | null,
): OrderTrackingJourney {
  const path = Array.isArray(order.logisticsPath) ? order.logisticsPath : [];
  const currentIndex = findFlaggedIndex(path, "isCurrentLocation");
  const destIndex = findFlaggedIndex(path, "isDestination");
  const current = currentIndex >= 0 ? path[currentIndex] : undefined;
  const dest = destIndex >= 0 ? path[destIndex] : undefined;

  const hasLiveShipment = Boolean(order.shipmentId);
  const statusAllowsTransit = IN_TRANSIT_STATUSES.has(
    String(order.orderStatus || ""),
  );
  const currentDiffersFromDest =
    currentIndex >= 0 &&
    destIndex >= 0 &&
    currentIndex !== destIndex &&
    !sameStop(current, dest);

  const isInTransit =
    hasLiveShipment && statusAllowsTransit && currentDiffersFromDest;

  const dispatchTime = formatJourneyTimestamp(order.metaInfo?.dispatchTime);
  const pickupTime = formatJourneyTimestamp(
    shipment?.deliveryTracking?.timeline?.pickedUpAt,
  );
  const bookedTime = formatJourneyTimestamp(
    shipment?.deliveryBooking?.bookedAt,
  );
  const leftSourceTime = dispatchTime || pickupTime || bookedTime;

  const lastUpdatedRaw =
    shipment?.deliveryTracking?.lastUpdatedAt ||
    order.metaInfo?.dispatchTime ||
    latestArrivedAt(path);
  const lastUpdatedLabel = formatJourneyTimestamp(lastUpdatedRaw);

  const displayPath = orderLogisticsPathForDisplay(path);
  const currentDisplayIndex = displayPath.findIndex(
    ({ originalIndex }) => originalIndex === currentIndex,
  );
  const firstArrivedDisplayIndex = displayPath.findIndex(({ entry }) =>
    Boolean(entry.arrivedAt),
  );

  const nodes: JourneyNode[] = displayPath.map(
    ({ entry, originalIndex }, displayIndex) => {
      const name = stopName(entry, originalIndex);
      const isCurrent = originalIndex === currentIndex;
      const isDest = originalIndex === destIndex;
      const base = {
        id: entryKey(entry, originalIndex),
        name,
        cityState: cityState(entry),
        isCurrentLocation: isCurrent,
        isDestination: isDest,
        type: entry.type,
      };

      if (isCustomerDelivered(entry, order.orderStatus)) {
        return {
          ...base,
          statusLabel: "Delivered" as const,
          visual: "green" as const,
          timestamp: formatJourneyTimestamp(entry.arrivedAt),
        };
      }

      // Live leg: destination first, then current source (even if arrivedAt set).
      if (isDest && isInTransit) {
        return {
          ...base,
          statusLabel: "In Transit" as const,
          visual: "blue" as const,
          timestamp: lastUpdatedLabel || leftSourceTime,
        };
      }

      if (isCurrent && isInTransit) {
        return {
          ...base,
          statusLabel: "Dispatched" as const,
          visual: "green" as const,
          timestamp: leftSourceTime || formatJourneyTimestamp(entry.arrivedAt),
        };
      }

      if (entry.arrivedAt) {
        return {
          ...base,
          statusLabel: "Arrived" as const,
          visual: "green" as const,
          timestamp: formatJourneyTimestamp(entry.arrivedAt),
        };
      }

      if (isCurrent && !isInTransit) {
        return {
          ...base,
          statusLabel: "At Stop" as const,
          visual: "neutral" as const,
          timestamp: formatJourneyTimestamp(entry.arrivedAt) || leftSourceTime,
        };
      }

      if (isDest && !isInTransit) {
        return {
          ...base,
          statusLabel: "Pending" as const,
          visual: "grey" as const,
        };
      }

      // Earlier origin before the first timed stop (or before current on live leg).
      // Do not reuse latest dispatchTime when this stop has no arrivedAt.
      const isBeforeFirstArrival =
        firstArrivedDisplayIndex >= 0 &&
        displayIndex < firstArrivedDisplayIndex;
      const isBeforeCurrent =
        currentDisplayIndex >= 0 && displayIndex < currentDisplayIndex;
      if (isBeforeFirstArrival || isBeforeCurrent) {
        return {
          ...base,
          statusLabel: "Dispatched" as const,
          visual: "green" as const,
          timestamp: formatJourneyTimestamp(entry.arrivedAt),
        };
      }

      return {
        ...base,
        statusLabel: "Pending" as const,
        visual: "grey" as const,
      };
    },
  );

  const connectors: JourneyConnectorVisual[] = [];
  for (let i = 0; i < nodes.length - 1; i++) {
    const next = nodes[i + 1];
    if (next.statusLabel === "In Transit") {
      connectors.push("blue");
    } else if (
      next.statusLabel === "Pending" ||
      next.visual === "grey" ||
      next.visual === "neutral"
    ) {
      connectors.push("grey");
    } else {
      connectors.push("green");
    }
  }

  const currentName = current ? stopName(current, currentIndex) : "";
  const destName = dest ? stopName(dest, destIndex) : "";

  let overallBadge = "PENDING";
  if (
    order.orderStatus === OrderStatus.DELIVERED ||
    nodes.some((n) => n.statusLabel === "Delivered")
  ) {
    overallBadge = "DELIVERED";
  } else if (isInTransit) {
    overallBadge = "IN TRANSIT";
  } else if (
    order.orderStatus === OrderStatus.READY_TO_DISPATCH &&
    currentName &&
    (!dest || !hasLiveShipment)
  ) {
    overallBadge = `AT ${currentName.toUpperCase()}`;
  } else if (
    order.orderStatus === OrderStatus.DISPATCHED ||
    order.orderStatus === OrderStatus.SHIPPED
  ) {
    overallBadge = "DISPATCHED";
  } else if (currentName) {
    overallBadge = `AT ${currentName.toUpperCase()}`;
  }

  let bannerText = "Tracking details will appear as the order moves.";
  if (
    order.orderStatus === OrderStatus.DELIVERED ||
    nodes.some((n) => n.statusLabel === "Delivered")
  ) {
    const customerNode = nodes.find((n) => n.type === "CUSTOMER");
    bannerText = `Order delivered to ${customerNode?.name || "customer"}.`;
  } else if (isInTransit && currentName && destName) {
    bannerText = `Order has left ${currentName} and is currently in transit to ${destName}.`;
  } else if (currentName && destName && !isInTransit) {
    bannerText = `Order is at ${currentName}. Next stop: ${destName}.`;
  } else if (
    currentName &&
    !dest &&
    (order.orderStatus === OrderStatus.READY_TO_DISPATCH ||
      Boolean(current?.arrivedAt) ||
      nodes.some((n) => n.statusLabel === "Arrived" && n.isCurrentLocation))
  ) {
    bannerText = `Order arrived at ${currentName}. Waiting for next destination.`;
  } else if (currentName) {
    bannerText = `Order is at ${currentName}.`;
  }

  const trackingUrl =
    shipment?.deliveryBooking?.trackingUrl ||
    order.metaInfo?.dispatchRider?.trackingUrl;
  const riderName =
    shipment?.deliveryTracking?.deliveryAgent?.name ||
    order.metaInfo?.dispatchRider?.name;
  const riderPhone =
    shipment?.deliveryTracking?.deliveryAgent?.phoneNumber ||
    order.metaInfo?.dispatchRider?.phone;

  return {
    orderNo: order.orderNo || "",
    nodes,
    connectors,
    overallBadge,
    lastUpdatedLabel,
    bannerText,
    isInTransit,
    trackingUrl,
    riderName,
    riderPhone,
  };
}
